/**
 * The placeholder grammar of ADR-119 decision 2. A value's text holds named placeholders, `{name}`, whose names are
 * `camelCase` ASCII with no dot; `{{` and `}}` stand for literal braces. A value that does not follow the grammar
 * is refused with the problem and the offset it was met at, so a catalogue check can name it and `say` can fall
 * back.
 */

/**
 * The longest placeholder name, in characters.
 */
export const MAX_PLACEHOLDER_NAME_CHARS = 32;

/**
 * A placeholder's name: `camelCase` ASCII, starting with a lower-case letter, with no dot.
 */
export const PLACEHOLDER_NAME_PATTERN = /^[a-z][a-zA-Z0-9]{0,31}$/;

/**
 * One part of a parsed value: literal text, with escapes already resolved, or a placeholder's name.
 */
export type TemplatePart = { readonly text: string } | { readonly name: string };

/**
 * Why a value's text does not parse: a name holding a dot, any other name the grammar refuses, an opening brace that
 * never closes, or a closing brace with no opening one that is not doubled.
 */
export type TemplateProblem = "dottedName" | "invalidName" | "unclosedBrace" | "strayClosingBrace";

/**
 * A parsed value, or why it does not parse. `at` is the UTF-16 offset of the brace the problem starts at; `name` is
 * the text between the braces for a name the grammar refuses.
 */
export type ParsedTemplate =
  | { readonly ok: true; readonly parts: readonly TemplatePart[] }
  | { readonly ok: false; readonly problem: "dottedName" | "invalidName"; readonly at: number; readonly name: string }
  | { readonly ok: false; readonly problem: "unclosedBrace" | "strayClosingBrace"; readonly at: number };

/**
 * Whether a value is a placeholder name the grammar admits.
 *
 * @param value Anything.
 * @returns True for a `camelCase` name of at most `MAX_PLACEHOLDER_NAME_CHARS` with no dot.
 */
export function isPlaceholderName(value: unknown): value is string {
  return typeof value === "string" && PLACEHOLDER_NAME_PATTERN.test(value);
}

/**
 * Parses a value's text into literal parts and placeholders, left to right: `{{` is a literal `{`, `}}` a literal
 * `}`, and `{` otherwise opens a placeholder that runs to the next `}`. Adjacent literal text is joined into one
 * part.
 *
 * @param text The value's text.
 * @returns The parts, or the first problem met.
 */
export function parseTemplate(text: string): ParsedTemplate {
  const parts: TemplatePart[] = [];
  let literal = "";
  let index = 0;
  while (index < text.length) {
    const char = text.charAt(index);
    if (char === "{") {
      if (text.charAt(index + 1) === "{") {
        literal += "{";
        index += 2;
        continue;
      }
      const close = text.indexOf("}", index + 1);
      if (close < 0) {
        return { ok: false, problem: "unclosedBrace", at: index };
      }
      const name = text.slice(index + 1, close);
      if (!PLACEHOLDER_NAME_PATTERN.test(name)) {
        return { ok: false, problem: name.includes(".") ? "dottedName" : "invalidName", at: index, name };
      }
      if (literal !== "") {
        parts.push({ text: literal });
        literal = "";
      }
      parts.push({ name });
      index = close + 1;
      continue;
    }
    if (char === "}") {
      if (text.charAt(index + 1) === "}") {
        literal += "}";
        index += 2;
        continue;
      }
      return { ok: false, problem: "strayClosingBrace", at: index };
    }
    literal += char;
    index += 1;
  }
  if (literal !== "") {
    parts.push({ text: literal });
  }
  return { ok: true, parts };
}

/**
 * The placeholder names a value's text uses, each once, in order of first use.
 *
 * @param text The value's text.
 * @returns The names, or null when the text does not parse.
 */
export function templateNames(text: string): readonly string[] | null {
  const parsed = parseTemplate(text);
  if (!parsed.ok) {
    return null;
  }
  const names: string[] = [];
  for (const part of parsed.parts) {
    if ("name" in part && !names.includes(part.name)) {
      names.push(part.name);
    }
  }
  return names;
}
